import type { Usuario, PapelUsuario } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { UsuariosApi } from './usuarios.api.js';

type Modo = 'criar' | 'editar';

const PAPEIS: Array<{ valor: PapelUsuario; rotulo: string }> = [
  { valor: 'admin', rotulo: 'Administrador' },
  { valor: 'financeiro', rotulo: 'Financeiro' },
  { valor: 'producao', rotulo: 'Produção' },
  { valor: 'parceiros', rotulo: 'Parceiros' },
  { valor: 'marketing', rotulo: 'Marketing' },
  { valor: 'suporte', rotulo: 'Suporte' },
];

/**
 * `<erp-usuario-form-modal>` — dois modos:
 * - `criar`: `POST /auth/usuarios` (nome/e-mail/senha inicial/papel).
 * - `editar`: usuário já existe, só papel e status são editáveis aqui
 *   (`PATCH /auth/usuarios/:id/papel` e `.../status`) — nome/e-mail/senha
 *   não têm rota de edição no backend (fora de escopo, não inventado
 *   aqui). Duas chamadas independentes, uma por campo alterado — nunca
 *   uma chamada "genérica de update" que o backend não expõe.
 */
export class ErpUsuarioFormModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _usuariosApi: UsuariosApi | null = null;
  private _aoSalvar: ((usuario: Usuario) => void) | null = null;

  private aberto = false;
  private modo: Modo = 'criar';
  private usuarioAtual: Usuario | null = null;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set usuariosApi(valor: UsuariosApi) {
    this._usuariosApi = valor;
  }

  set aoSalvar(valor: (usuario: Usuario) => void) {
    this._aoSalvar = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  abrirParaCriar(): void {
    this.modo = 'criar';
    this.usuarioAtual = null;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  abrirParaEditar(usuario: Usuario): void {
    this.modo = 'editar';
    this.usuarioAtual = usuario;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeterCriacao(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._usuariosApi || this.enviando) return;

    const nome = this.valorCampo('#nome').trim();
    const email = this.valorCampo('#email').trim();
    const senha = this.valorCampo('#senha');
    const papel = this.valorCampo('#papel') as PapelUsuario;

    if (!nome || !email || !senha) {
      this.mensagemErro = 'nome, e-mail e senha são obrigatórios';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const usuario = await this._usuariosApi.criar({ nome, email, senha, papel });
      this.aberto = false;
      this._aoSalvar?.(usuario);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private async salvarEdicao(): Promise<void> {
    if (!this._usuariosApi || !this.usuarioAtual || this.enviando) return;

    const papelNovo = this.valorCampo('#papel-edicao') as PapelUsuario;
    const ativoNovo = (this.root.querySelector('#ativo-edicao') as HTMLInputElement | null)?.checked ?? true;

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      let usuario = this.usuarioAtual;
      if (papelNovo !== usuario.papel) {
        usuario = await this._usuariosApi.alterarPapel(usuario.id, papelNovo);
      }
      if (ativoNovo !== usuario.ativo) {
        usuario = await this._usuariosApi.alterarStatus(usuario.id, ativoNovo);
      }
      this.aberto = false;
      this._aoSalvar?.(usuario);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível salvar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement | HTMLSelectElement>(seletor)?.value ?? '';
  }

  private render(): void {
    const modo = this.modo;
    const usuario = this.usuarioAtual;

    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .linha-ativo { flex-direction: row; align-items: center; gap: var(--pg-space-3, 12px); }
        .linha-ativo input { width: auto; padding: 0; }
        .info { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-primario, #f2f3f5); }
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">${modo === 'criar' ? 'Novo usuário' : 'Editar usuário'}</span>
        ${
          modo === 'criar'
            ? `<form id="form-criar" novalidate>
                 <label>Nome <input id="nome" type="text" required maxlength="255" /></label>
                 <label>E-mail <input id="email" type="email" required maxlength="255" /></label>
                 <label>Senha inicial <input id="senha" type="password" required minlength="8" maxlength="512" autocomplete="new-password" /></label>
                 <label>Papel
                   <select id="papel">
                     ${PAPEIS.map((p) => `<option value="${p.valor}">${escaparHtml(p.rotulo)}</option>`).join('')}
                   </select>
                 </label>
                 <span class="erro">${escaparHtml(this.mensagemErro)}</span>
               </form>`
            : `<form id="form-editar" novalidate>
                 <p class="info"><strong>${escaparHtml(usuario?.nome ?? '')}</strong> — ${escaparHtml(usuario?.email ?? '')}</p>
                 <label>Papel
                   <select id="papel-edicao">
                     ${PAPEIS.map((p) => `<option value="${p.valor}" ${p.valor === usuario?.papel ? 'selected' : ''}>${escaparHtml(p.rotulo)}</option>`).join('')}
                   </select>
                 </label>
                 <label class="linha-ativo"><input id="ativo-edicao" type="checkbox" ${usuario?.ativo ? 'checked' : ''} /> Ativo</label>
                 <span class="erro">${escaparHtml(this.mensagemErro)}</span>
               </form>`
        }
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>${modo === 'criar' ? 'Criar' : 'Salvar'}</pg-button>
        </div>
      </pg-modal>
    `;

    const submeter = (evento: Event) => {
      if (modo === 'criar') void this.aoSubmeterCriacao(evento);
      else {
        evento.preventDefault();
        void this.salvarEdicao();
      }
    };

    this.root.querySelector('form')?.addEventListener('submit', submeter);
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', submeter);
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-usuario-form-modal', ErpUsuarioFormModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-usuario-form-modal': ErpUsuarioFormModal;
  }
}
